/**
 * Megolm session management and key distribution, browser side.
 *
 * Each channel has ONE outbound session we encrypt every message with. Its
 * key has to reach every member *device* - including our own other devices,
 * which is what makes the phone able to read what the browser sent - as an
 * Olm-encrypted `m.room_key` on the to-device rail.
 *
 * The payload shape mirrors the phone's exactly, because the phone's
 * `toDevice.ts` is what imports it:
 *
 *   { type: "m.room_key",
 *     content: { algorithm, room_id, session_id, session_key } }
 *
 * ## Sharing is incremental, not per-message
 *
 * `sharedWith` records which devices already hold the key. Without that the
 * browser would send one to-device message per member device per message,
 * which on a group of ten people with two devices each is twenty extra
 * round trips to say something already said.
 *
 * ## Rotation
 *
 * A new device joining is NOT a reason to rotate - it is a reason to share
 * the existing session, which is what `shareExistingSession` on the phone
 * does. Rotation is for a device *leaving*, which this cannot observe yet;
 * the browser reuses its session and the phone remains the authority on
 * rotation.
 */
import { loadCrypto } from '@nsibi/e2ee-wasm';

import { api } from '../lib/api';
import { getDeviceId } from '../lib/device';
import { getSession } from '../lib/session';
import { encryptOlmTo, queryDevices, type DeviceKey } from './olmSession';
import {
  loadAccount,
  loadMegolmSession,
  loadOutboundSession,
  saveMegolmSession,
  saveOutboundSession,
  type OutboundSession,
} from './store';

export const MEGOLM_ALGORITHM = 'm.megolm.v1.aes-sha2';
export const ROOM_KEY_TYPE = 'm.room_key';

export type Envelope = {
  algorithm: string;
  sender_key: string;
  session_id: string;
  ciphertext: string;
};

const tag = (d: DeviceKey) => `${d.userId}:${d.deviceId}`;

/**
 * Every device that should be able to read this channel: all members' devices
 * plus our own others, minus this browser.
 */
async function recipientDevices(memberIds: number[]): Promise<DeviceKey[]> {
  const me = getSession()?.user.id;
  const myDeviceId = getDeviceId();
  // Always include ourselves in the query so our own other devices - the
  // phone that will want to read this - are covered.
  const ids = Array.from(new Set([...memberIds, ...(me ? [me] : [])]));
  const all = await queryDevices(ids);
  return all.filter((d) => !(d.userId === me && d.deviceId === myDeviceId));
}

/** Olm-encrypt the room key to each device and post one fan-out batch. */
async function shareRoomKey(
  channelId: string,
  sessionId: string,
  sessionKey: string,
  devices: DeviceKey[],
): Promise<DeviceKey[]> {
  if (devices.length === 0) return [];

  const payload = {
    type: ROOM_KEY_TYPE,
    content: {
      algorithm: MEGOLM_ALGORITHM,
      room_id: channelId,
      session_id: sessionId,
      session_key: sessionKey,
    },
  };

  const reached: DeviceKey[] = [];
  const messages: Array<{
    target_user_id: number;
    target_device_id: string;
    message_type: string;
    ciphertext: string;
  }> = [];

  for (const device of devices) {
    const ciphertext = await encryptOlmTo(device, payload);
    if (ciphertext === null) continue; // no keys published, or pool exhausted
    messages.push({
      target_user_id: device.userId,
      target_device_id: device.deviceId,
      message_type: ROOM_KEY_TYPE,
      ciphertext,
    });
    reached.push(device);
  }

  if (messages.length === 0) {
    console.warn(
      `[nsibi] room key ${sessionId.slice(0, 10)}: reached 0/${devices.length} device(s) - none could be sealed to`,
    );
    return [];
  }

  await api('/v1/sendToDevice/', {
    method: 'POST',
    body: { sender_device_id: getDeviceId(), messages },
  });
  console.log(
    `[nsibi] room key ${sessionId.slice(0, 10)} shared with ${reached.length}/${devices.length}: ${reached
      .map((d) => `${d.userId}:${d.deviceId.slice(0, 6)}`)
      .join(', ')}`,
  );
  return reached;
}

/**
 * The session to encrypt this channel with, created and distributed if it
 * does not exist, and topped up for any device that has joined since.
 */
/** Mint a fresh outbound Megolm session, self-import it so we can read our own
 *  echoes, and distribute the key to every recipient device. Used for a brand
 *  new channel and to replace a corrupt persisted session. */
async function createAndDistribute(
  channelId: string,
  devices: DeviceKey[],
): Promise<OutboundSession> {
  const core = await loadCrypto();
  const created = core.createOutboundGroupSession();
  const mine = core.createInboundGroupSession(created.session_key);
  await saveMegolmSession(mine.session_id, mine.session_pickle);

  const reached = await shareRoomKey(
    channelId,
    created.session_id,
    created.session_key,
    devices,
  );

  const row: OutboundSession = {
    sessionId: created.session_id,
    pickle: created.session_pickle,
    sessionKey: created.session_key,
    sharedWith: reached.map(tag),
  };
  await saveOutboundSession(channelId, row);
  return row;
}

/**
 * The id a shared key material actually derives. We tag messages with the
 * outbound session's own id, but hand recipients ``sessionKey``; if a persisted
 * row is corrupt those two diverge, and the recipient files the key under an id
 * our messages never carry - the "waiting for this message" that no re-share
 * can fix. Returns null when the key cannot be interpreted at all.
 */
function derivedSessionId(core: Awaited<ReturnType<typeof loadCrypto>>, key: string): string | null {
  try {
    return core.createInboundGroupSession(key).session_id;
  } catch {
    try {
      return core.importInboundGroupSession(key).session_id;
    } catch {
      return null;
    }
  }
}

async function ensureOutboundSession(
  channelId: string,
  memberIds: number[],
): Promise<OutboundSession> {
  const core = await loadCrypto();
  const devices = await recipientDevices(memberIds);
  const existing = await loadOutboundSession(channelId);

  if (existing) {
    const me = getSession()?.user.id;

    // The key we would hand recipients: the stored fresh key, or - for a row
    // from an older build that lacks it - reconstructed from our self-imported
    // inbound copy.
    let keyToShare = existing.sessionKey;
    if (!keyToShare) {
      const pickle = await loadMegolmSession(existing.sessionId);
      if (pickle) {
        try {
          keyToShare = core.exportInboundGroupSession(pickle);
        } catch {
          /* leave undefined */
        }
      }
    }

    // Health check: the key must derive the SAME id we tag messages with.
    // A corrupt/legacy row where they diverge is unrecoverable by re-sharing
    // (the recipient stores the key under the wrong id), so rotate to a fresh,
    // internally-consistent session. A no-op for healthy sessions.
    const derived = keyToShare ? derivedSessionId(core, keyToShare) : null;
    if (!keyToShare || derived !== existing.sessionId) {
      console.warn(
        `[nsibi] outbound session for ${channelId.slice(0, 8)} is inconsistent ` +
          `(tagged ${existing.sessionId.slice(0, 8)}, key derives ${derived?.slice(0, 8) ?? 'none'}) - rotating`,
      );
      return createAndDistribute(channelId, devices);
    }

    // Backfill a reconstructed key so future sends use it directly.
    if (existing.sessionKey !== keyToShare) {
      await saveOutboundSession(channelId, { ...existing, sessionKey: keyToShare });
    }

    // A device is "missing" if we never recorded reaching it OR it is one of
    // our OWN devices (the phone) - we never trust sharedWith for our own
    // devices, so the key is re-handed to the phone every send (it dedupes on
    // import). A latecomer gets the RUNNING session, not a rotation.
    const missing = devices.filter(
      (d) => !existing.sharedWith.includes(tag(d)) || d.userId === me,
    );
    if (missing.length === 0) return existing;

    const reached = await shareRoomKey(channelId, existing.sessionId, keyToShare, missing);
    if (reached.length === 0) return existing;

    const updated = {
      ...existing,
      sessionKey: keyToShare,
      sharedWith: Array.from(new Set([...existing.sharedWith, ...reached.map(tag)])),
    };
    await saveOutboundSession(channelId, updated);
    return updated;
  }

  return createAndDistribute(channelId, devices);
}

/**
 * Encrypt one message for a channel. Returns the envelope to POST.
 *
 * The plaintext wrapper matches the phone's `encryptMessage` exactly -
 * `{ type: "m.text", content }` - because the phone's decrypt unwraps that
 * shape and would otherwise render raw JSON.
 */
export async function encryptForChannel(
  channelId: string,
  memberIds: number[],
  plaintext: string,
): Promise<Envelope> {
  const core = await loadCrypto();
  const session = await ensureOutboundSession(channelId, memberIds);

  const payload = JSON.stringify({ type: 'm.text', content: plaintext });
  const encrypted = core.groupEncrypt(session.pickle, payload);

  // The ratchet advanced; persist or the next message reuses a spent index.
  await saveOutboundSession(channelId, {
    ...session,
    pickle: encrypted.session_pickle,
  });

  const account = await loadAccount();
  const senderKey = account ? core.accountIdentityKeys(account).curve25519 : '';

  return {
    algorithm: MEGOLM_ALGORITHM,
    sender_key: senderKey,
    session_id: session.sessionId,
    ciphertext: encrypted.ciphertext,
  };
}
